import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderContextType = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "dark" | "light";
};

const ThemeProviderContext = createContext<ThemeProviderContextType | undefined>(undefined);

function getThemeByTime(): "dark" | "light" {
  const hour = new Date().getHours();
  // 6:00 - 17:59 = light, 18:00 - 5:59 = dark
  return hour >= 6 && hour < 18 ? "light" : "dark";
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "tupja-theme",
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(storageKey) as Theme) || defaultTheme;
    }
    return defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() => {
    if (theme === "system") {
      return getThemeByTime();
    }
    return theme;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    if (theme === "system") {
      const timeBasedTheme = getThemeByTime();
      root.classList.add(timeBasedTheme);
      setResolvedTheme(timeBasedTheme);
    } else {
      root.classList.add(theme);
      setResolvedTheme(theme);
    }
  }, [theme]);

  // Auto-update when in system mode
  useEffect(() => {
    if (theme !== "system") return;

    const checkAndUpdateTheme = () => {
      const timeBasedTheme = getThemeByTime();
      const root = window.document.documentElement;
      
      if (!root.classList.contains(timeBasedTheme)) {
        root.classList.remove("light", "dark");
        root.classList.add(timeBasedTheme);
        setResolvedTheme(timeBasedTheme);
      }
    };

    // Check every minute
    const interval = setInterval(checkAndUpdateTheme, 60000);
    
    return () => clearInterval(interval);
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);
    },
    resolvedTheme,
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
